import type {
  Coverage,
  OperatingStatus,
  PublicPlatform,
} from '../../platform/platform.types';
import { regionOf } from '../../shared/feed/model';
import { REGIONS, type Region } from './data';
import { urgencyOf, type Urgency } from './deco';
import { cd, spoken, SYMBOL } from './format';

/**
 * The public platform directory's rules (/platforms), shared by the server
 * render and the page's script: the fee gate, the region pills, the cells'
 * wording and colours, and the Details dialog's list and copy. No Node or DOM
 * APIs in here. The view model is built in view.ts.
 */

/** The page's h1, which its CollectionPage names it by. */
export const PLATFORMS_H1 = 'Every equity-crowdfunding platform we track';

// ---------------------------------------------------------------------------
// The fee gate
// ---------------------------------------------------------------------------

/**
 * What a gated fee cell says, blurred. Not a fee and not a guess at one:
 * crawlers index the page, and so does a reader with no stylesheet.
 */
export const FEE_PLACEHOLDER = 'Shown with a free account';

export type FeeCell =
  | { gated: true; text: string }
  | { gated: false; text: string; missing: boolean };

/**
 * The gate. A signed-in reader, on any plan, Free included, sees what the
 * platform charges; anyone else gets the placeholder, and the fee itself is
 * never in what the server sends them. Every cell is gated alike, those with
 * no fee stated too, so the blur says nothing about which ones have one.
 */
export function feeCell(fee: string | null, signedIn: boolean): FeeCell {
  if (!signedIn) return { gated: true, text: FEE_PLACEHOLDER };
  return fee
    ? { gated: false, text: fee, missing: false }
    : { gated: false, text: 'Not stated', missing: true };
}

// ---------------------------------------------------------------------------
// Regions
// ---------------------------------------------------------------------------

export type RegionFilter = 'All' | Region;

export const REGION_FILTERS: RegionFilter[] = ['All', ...REGIONS];

/** `?region=` as the page reads it: a pill's label in any case, else All. */
export function parseRegion(value: unknown): RegionFilter {
  if (typeof value !== 'string') return 'All';
  const wanted = value.trim().toLowerCase();
  return REGION_FILTERS.find((r) => r.toLowerCase() === wanted) ?? 'All';
}

/** The pill a platform's country falls under; null for one under All only. */
export function platformRegion(country: string | null): Region | null {
  const region = regionOf(country);
  return region === 'Other' ? null : region;
}

export function inRegion(region: Region | null, filter: RegionFilter): boolean {
  return filter === 'All' || region === filter;
}

/**
 * Each pill with how many platforms it counts: the platforms read today in
 * its region, as the site's facts count them (site/facts.ts), so All says
 * what every page says. Planned rows show under their pill, marked
 * Planned, and are not counted, as the stats line does not count them.
 */
export function regionCounts(
  regions: (Region | null)[],
): { region: RegionFilter; count: number }[] {
  return REGION_FILTERS.map((region) => ({
    region,
    count: regions.filter((r) => inRegion(r, region)).length,
  }));
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** Each coverage's pill: its words and its colour class (`cov-<tone>`). */
export const COVERAGE: Record<Coverage, { label: string; tone: string }> = {
  FILINGS: { label: 'Live via filings', tone: 'filings' },
  SITE: { label: 'Live via site', tone: 'site' },
  PARTIAL: { label: 'Partial', tone: 'partial' },
  PLANNED: { label: 'Planned', tone: 'planned' },
  // The public directory leaves these out (PlatformService.publicDirectory).
  NONE: { label: 'Not covered', tone: 'planned' },
};

export interface Note {
  glyph: '▲' | '■' | '●';
  tone: 'urgent' | 'critical' | 'muted';
  text: string;
}

const NOTE_MARKS: Record<OperatingStatus, Pick<Note, 'glyph' | 'tone'>> = {
  WATCH: { glyph: '▲', tone: 'urgent' },
  WINDING_DOWN: { glyph: '■', tone: 'critical' },
  ACTIVE: { glyph: '●', tone: 'muted' },
  CLOSED: { glyph: '●', tone: 'muted' },
};

/**
 * The line under a platform's name: why it is on Watch or winding down, or
 * what its live count leaves out (a lending portal's loans). Its mark follows
 * the platform's status, so the shape says it as well as the colour.
 */
export function noteOf(
  platform: Pick<PublicPlatform, 'status' | 'statusNote' | 'scopeNote'>,
): Note | null {
  const text = platform.statusNote ?? platform.scopeNote;
  return text ? { ...NOTE_MARKS[platform.status], text } : null;
}

/** "$100", "€1,000", "1,000 PLN"; the platform's own words where it states no figure. */
export function minTicketCell(
  platform: Pick<PublicPlatform, 'minTicket' | 'minTicketNote' | 'currency'>,
): { text: string; missing: boolean } {
  const { minTicket, currency, minTicketNote } = platform;
  if (minTicket !== null) {
    const amount = Math.round(minTicket).toLocaleString('en-GB');
    const symbol = currency ? SYMBOL[currency] : undefined;
    const text = symbol
      ? `${symbol}${amount}`
      : `${amount}${currency ? ` ${currency}` : ''}`;
    return { text, missing: false };
  }
  if (minTicketNote) return { text: minTicketNote, missing: false };
  return { text: 'Not stated', missing: true };
}

/** Live now: a count, muted at 0, and a dash where nothing reads the platform yet. */
export function liveCell(
  platform: Pick<PublicPlatform, 'coverage' | 'liveRaises'>,
): { text: string; muted: boolean } {
  if (platform.coverage === 'PLANNED') return { text: '—', muted: true };
  return {
    text: String(platform.liveRaises),
    muted: platform.liveRaises === 0,
  };
}

// ---------------------------------------------------------------------------
// The Details dialog
// ---------------------------------------------------------------------------

/** How many live raises the dialog names. */
export const DIALOG_LIVE_MAX = 4;

/** A live raise as the dialog names it; `closesAt` in ms, null where none is stated. */
export interface DialogRaise {
  name: string;
  closesAt: number | null;
}

/**
 * What the page's script knows of each platform, from the JSON the server
 * puts in it (#pf-data). No fee, whoever is reading.
 */
export interface DetailsTarget {
  id: string;
  name: string;
  mono: string;
  region: Region | null;
  planned: boolean;
  liveCount: number;
  /** Soonest close first, at most DIALOG_LIVE_MAX. */
  live: DialogRaise[];
}

/**
 * Soonest close first, at most `max`. A raise that states no close comes
 * after every one that does, by name, so the list is the same on every load.
 */
export function soonestClosing<T extends DialogRaise>(
  raises: T[],
  max = DIALOG_LIVE_MAX,
): T[] {
  return [...raises]
    .sort(
      (a, b) =>
        (a.closesAt ?? Infinity) - (b.closesAt ?? Infinity) ||
        a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }),
    )
    .slice(0, max);
}

export function dialogTitle(name: string): string {
  return `Details on ${name}`;
}

export function dialogBody(name: string): string {
  return `Create a free account and ${name} raises go into your Weekly Sonar. Start the Alerts trial to hear the minute one launches.`;
}

/** The list box's head: "3 live on Wefunder now", or without a count at none. */
export function dialogHead(name: string, liveCount: number): string {
  return liveCount > 0
    ? `${liveCount} live on ${name} now`
    : `Live on ${name} now`;
}

/** What the list box says with nothing to list. */
export function dialogEmpty(planned: boolean): string {
  return planned
    ? 'Coverage is planned. We’ll start sending its raises when it goes live.'
    : 'Nothing live right now. Its next raise goes into your Weekly Sonar.';
}

/** "closes in 2d 06:59:55", in its urgency's colour; no countdown without a close. */
export function closesIn(
  closesAt: number | null,
  now: number,
): { text: string; label: string; urgency: Urgency | null } {
  if (closesAt === null) {
    return {
      text: 'no close date stated',
      label: 'No close date stated',
      urgency: null,
    };
  }
  const left = closesAt - now;
  return {
    text: `closes in ${cd(left)}`,
    label: spoken(left),
    urgency: urgencyOf(left),
  };
}

// ---------------------------------------------------------------------------
// Where the buttons go
// ---------------------------------------------------------------------------

/** Every signup from this page says so, for the dashboard and its events. */
export const FROM = 'from=platforms';

/** The sign-up page (web/src/auth), where the hero's form posts. */
export const SIGNUP_PATH = '/signup';

/** The sign-up page, with the address typed here filled in. */
export function signupHref(email = ''): string {
  const typed = email.trim();
  return `${SIGNUP_PATH}?${FROM}${typed ? `&email=${encodeURIComponent(typed)}` : ''}`;
}

/** The dialog's Create free account: onboarding, from this page. */
export const ONBOARDING_HREF = `/get-started?${FROM}`;

/** Log in, then back here, fees showing. */
export const LOGIN_HREF = `/login?redirect_url=${encodeURIComponent('/platforms')}`;
